import { nextSort, sortRows } from "../sortable";
import { useDispatch, useSelector } from "react-redux";

import AdminFilterHeader from "../../components/AdminFilterHeader/AdminFilterHeader";
import { AdminTable } from "../AdminTable";
import { Button } from "reactstrap";
import PropTypes from "prop-types";
import TemplateModal from "./TemplateModal";
import { addDangerAlert } from "../../actions/alerts";
import { jsonContentType } from "../../services/backendRequests";
import { useFetch } from "../../hooks/useFetch";
import { useState } from "react";

const handleResponse = (data, dispatch) => {
  if (!data.ok)
    data.json().then((d) => {
      dispatch(addDangerAlert(d.message));
    });

  return data;
};

const newTemplate = (template, identity, dispatch) =>
  fetch("/api/upload_templates", {
    method: "POST",
    body: JSON.stringify(template),
    headers: jsonContentType(identity),
  }).then((data) => handleResponse(data, dispatch));

const updateTemplate = (template, identity, dispatch) =>
  fetch(`/api/upload_templates/${template.id}`, {
    method: "PUT",
    body: JSON.stringify(template),
    headers: jsonContentType(identity),
  }).then((data) => handleResponse(data, dispatch));

const deleteTemplate = (id, identity, dispatch) =>
  fetch(`/api/upload_templates/${id}`, {
    method: "DELETE",
    headers: jsonContentType(identity),
  }).then((data) => handleResponse(data, dispatch));

function TemplateRow({ template, onDelete, onUpdate }) {
  const { id, name, vector, active } = template;
  const [openEditModal, setEditModal] = useState(false);

  return (
    <tr key={id}>
      <td>{id}</td>
      <td>{name}</td>
      <td>{vector}</td>
      <td>
        {active ? (
          <i className="fa-solid fa-lg fa-check text-success" />
        ) : (
          <i className="fa-solid fa-lg fa-xmark text-danger" />
        )}
      </td>
      <td>
        <div className="admin-actions">
          <Button
            color="link"
            data-tooltip-id="tooltip"
            data-tooltip-content="Edit template information"
            onClick={() => setEditModal(true)}
          >
            <i className="fa-solid fa-lg fa-pencil" />
          </Button>
          <Button
            color="link"
            className="admin-actions__danger"
            data-tooltip-id="tooltip"
            data-tooltip-content="Delete template"
            data-tooltip-variant="error"
            onClick={() => onDelete(id)}
          >
            <i className="fa-solid fa-lg fa-trash-can" />
          </Button>
        </div>

        <TemplateModal
          id={id}
          defaults={template}
          onSubmit={(t) => onUpdate(t)}
          isOpen={openEditModal}
          setOpen={setEditModal}
        />
      </td>
    </tr>
  );
}

export default function UploadTemplates() {
  const [templates, { refetch, loading }] = useFetch("/api/upload_templates");
  const noTemplates = !loading && (templates ?? []).length === 0;
  const identity = useSelector((state) => state.user.identity);
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ sortKey: "name", sortDir: "asc" });
  const handleFilterChange = (event) => {
    setSearch(event.target.value);
  };
  const handleSort = (column) => setSort((prev) => nextSort(prev, column));
  const filteredTemplates = !templates
    ? []
    : search === ""
    ? templates
    : templates.filter((template) => {
        return (
          template.name.toLowerCase().includes(search.toLowerCase()) ||
          template.vector.toLowerCase().includes(search.toLowerCase())
        );
      });
  const sortedTemplates = sortRows(
    filteredTemplates,
    sort.sortKey,
    sort.sortDir,
    (t, key) => {
      if (key === "id") return t.id;
      if (key === "active") return t.active ? 1 : 0;
      return t[key] || "";
    }
  );

  const columns = [
    { key: "id", label: "Id", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "vector", label: "Vector", sortable: true },
    { key: "active", label: "Active", sortable: true },
    { key: "actions", label: "Actions" },
  ];

  return (
    <div className="mt-3 ps-3">
      <AdminFilterHeader
        adminOnly
        onButtonClick={() => setOpenModal(true)}
        onFilterChange={handleFilterChange}
        onRefresh={refetch}
        refreshing={loading}
        search={search}
        buttonName="New template"
      />
      <AdminTable
        columns={columns}
        rows={sortedTemplates}
        loading={loading}
        sortKey={sort.sortKey}
        sortDir={sort.sortDir}
        onSort={handleSort}
        renderRow={(t) => (
          <TemplateRow
            key={t.id}
            template={t}
            onUpdate={(template) =>
              updateTemplate(template, identity, dispatch).then(() => refetch())
            }
            onDelete={(id) =>
              deleteTemplate(id, identity, dispatch).then(() => refetch())
            }
          />
        )}
      />
      {noTemplates ? <div className="m-2">No templates</div> : null}
      <TemplateModal
        isOpen={openModal}
        setOpen={setOpenModal}
        onSubmit={(e) => {
          newTemplate(e, identity, dispatch).then(() => refetch());
        }}
      />
    </div>
  );
}
TemplateRow.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    vector: PropTypes.string.isRequired,
    active: PropTypes.bool.isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
